/**
 * @file App.jsx
 * @description Componente raíz AGDYSA — sitio público + panel CMS /admin.
 */

import { useMemo, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import IdentityTheme from "./components/IdentityTheme";
import SEO from "./components/SEO";
import { getSeoForPath } from "./config/seoRoutes";
import Navbar from "./components/Navbar";
import BottomNav from "./components/BottomNav";
import HeroIdleChrome from "./components/HeroIdleChrome";
import SystemLoader from "./components/SystemLoader";
import ScrollToTop from "./components/ScrollToTop";
import { getSocialLinks, filterFooterItemsForAudience } from "./lib/socialPlatforms";
import { ResolveCmsIcon } from "./components/ResolveCmsIcon";

import ScientificResearchPage from "./pages/ScientificResearchPage";
import contentData from "./data/content.json";
import labsysoContentData from "./data/labsysoContent.json";
import Home from "./pages/Home";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import ContactPage from "./pages/ContactPage";
import LabsysoPage from "./pages/LabsysoPage";
import CredibilityPage from "./pages/CredibilityPage";
import CertificationsPage from "./pages/CertificationsPage";
import CalculatorsPage from "./modules/calculators/CalculatorsPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";

import { QuotationProvider } from "./context/QuotationContext";
import QuotationBasketDrawer from "./components/QuotationBasketDrawer";
import QuotationToast from "./components/QuotationToast";
import CookieConsentBanner from "./components/CookieConsentBanner";
import VisitTracker from "./components/VisitTracker";
import { useCookieConsentOptional } from "./context/CookieConsentContext";

import { useCmsDoc } from "./hooks/useCmsDoc";
import CmsLoginPage from "./admin/CmsLoginPage";
import CmsOtpPage from "./admin/CmsOtpPage";
import CmsSetPasswordPage from "./admin/CmsSetPasswordPage";
import CmsProtectedRoute from "./admin/CmsProtectedRoute";
import CmsLayout from "./admin/CmsLayout";
import CmsDashboardPage from "./admin/CmsDashboardPage";
import CmsIdentidadPage from "./admin/editors/CmsIdentidadPage";
import CmsHomePage from "./admin/editors/CmsHomePage";
import CmsFooterEditor from "./admin/editors/CmsFooterEditor";
import CmsAboutEditor from "./admin/editors/CmsAboutEditor";
import CmsCredibilityEditor from "./admin/editors/CmsCredibilityEditor";
import CmsServicesEditor from "./admin/editors/CmsServicesEditor";
import CmsResearchEditor from "./admin/editors/CmsResearchEditor";
import CmsLabsysoEditor from "./admin/editors/CmsLabsysoEditor";
import CmsUsersPage from "./admin/editors/CmsUsersPage";
import CmsIconsEditor from "./admin/editors/CmsIconsEditor";
import CmsCalculatorsEditor from "./admin/editors/CmsCalculatorsEditor";
import CmsNavigationEditor from "./admin/editors/CmsNavigationEditor";
import CmsClientsEditor from "./admin/editors/CmsClientsEditor";
import CmsLeadsEditor from "./admin/editors/CmsLeadsEditor";
import CmsQuejasEditor from "./admin/editors/CmsQuejasEditor";
import CmsInformesGate from "./admin/CmsInformesGate";
import CmsInformesPage from "./admin/editors/CmsInformesPage";
import CmsModuleGate from "./admin/CmsModuleGate";
import DocsLibraryPage from "./admin/DocsLibraryPage";
import CmsPrivacidadEditor from "./admin/editors/CmsPrivacidadEditor";

function PublicShell() {
  const [systemReady, setSystemReady] = useState(false);
  const location = useLocation();
  const isLabsysoPage = location.pathname.includes("/labsyso");
  const seo = getSeoForPath(location.pathname);

  const footerFallback = useMemo(
    () => ({
      socialMedia: contentData.socialMedia,
      socialLinks: null,
      extraLinks: [],
    }),
    []
  );
  const { data: footerCms } = useCmsDoc("site", "footer", footerFallback);

  const socialMediaLinks = isLabsysoPage
    ? labsysoContentData.labsyso.socialMedia
    : footerCms?.socialLinks?.length
      ? footerCms.socialLinks
      : footerCms?.socialMedia || contentData.socialMedia;

  const socialLinks = getSocialLinks(socialMediaLinks, { audience: "website" });
  const extraLinks = filterFooterItemsForAudience(
    footerCms?.extraLinks || [],
    "website"
  );
  const cookieConsent = useCookieConsentOptional();

  const legalLinks = [
    { id: "privacidad", label: "Aviso de privacidad", url: "/privacidad" },
    { id: "cookies", label: "Política de cookies", url: "/cookies" },
  ];

  return (
    <QuotationProvider>
      <HeroIdleChrome>
      <div className="relative min-h-screen bg-surface overflow-x-hidden font-body">
        <IdentityTheme />
        <SEO {...seo} />
        <ScrollToTop />
        <CookieConsentBanner />
        <VisitTracker />

        <AnimatePresence>
          {!systemReady && (
            <SystemLoader onComplete={() => setSystemReady(true)} />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {systemReady && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
            >
              <Navbar />
              <BottomNav />
              <QuotationBasketDrawer />
              <QuotationToast />
            </motion.div>
          )}
        </AnimatePresence>

        <main>
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<Home isReady={systemReady} />} />
              <Route path="/nosotros" element={<AboutPage />} />
              <Route path="/credibilidad" element={<CredibilityPage />} />
              <Route path="/certificaciones" element={<CertificationsPage />} />
              <Route path="/servicios" element={<ServicesPage />} />
              <Route path="/contacto" element={<ContactPage />} />
              <Route path="/labsyso" element={<LabsysoPage />} />
              <Route path="/investigacion" element={<ScientificResearchPage />} />
              <Route path="/calculadoras" element={<CalculatorsPage />} />
              <Route path="/privacidad" element={<PrivacyPolicyPage />} />
              <Route path="/cookies" element={<CookiePolicyPage />} />
            </Routes>
          </AnimatePresence>
        </main>

        <footer className="bg-[#050e08] text-[#8ba899] py-16 text-center border-t border-white/5 mt-auto">
          <div className="container mx-auto px-6">
            <div className="flex justify-center space-x-6 mb-8">
              {socialLinks.map(({ key, url, icon, Icon, label }) => (
                <a
                  key={key}
                  href={url}
                  target={url.startsWith("http") ? "_blank" : undefined}
                  rel={url.startsWith("http") ? "noopener noreferrer" : undefined}
                  aria-label={label}
                  className="text-white hover:text-brand-accent transition-colors inline-flex"
                >
                  {icon ? (
                    <ResolveCmsIcon icon={icon} size={24} className="text-white" alt={label} />
                  ) : Icon ? (
                    <Icon size={24} aria-hidden />
                  ) : null}
                </a>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-4 mb-6">
              {legalLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.url}
                  className="text-sm text-white/70 hover:text-brand-accent"
                >
                  {link.label}
                </a>
              ))}
              {cookieConsent ? (
                <button
                  type="button"
                  onClick={() => cookieConsent.openPreferences()}
                  className="text-sm text-white/70 hover:text-brand-accent"
                >
                  Configurar cookies
                </button>
              ) : null}
              {extraLinks.map((link) => (
                <a
                  key={link.id || link.url}
                  href={link.url}
                  target={link.external ? "_blank" : undefined}
                  rel={link.external ? "noopener noreferrer" : undefined}
                  className="text-sm text-white/70 hover:text-brand-accent"
                >
                  {link.label}
                </a>
              ))}
            </div>
            <p className="text-sm font-body tracking-wide">
              <span className="text-brand-accent font-bold">AGDYSA-LABSYSO</span>{" "}
              – Centro de Inteligencia y Salud Laboral.
            </p>
            <p className="text-sm font-body tracking-wide">
              <span className="text-brand-accent font-bold">© 2026 </span>. Todos
              los derechos reservados.
            </p>
          </div>
        </footer>
      </div>
      </HeroIdleChrome>
    </QuotationProvider>
  );
}

function AdminRoutes() {
  return (
    <Routes>
      <Route path="/admin/login" element={<CmsLoginPage />} />
      <Route path="/admin/otp" element={<CmsOtpPage />} />
      <Route element={<CmsProtectedRoute requireOtp />}>
        <Route path="/admin/set-password" element={<CmsSetPasswordPage />} />
        <Route path="/admin" element={<CmsLayout />}>
          <Route index element={<CmsDashboardPage />} />
          <Route
            path="clientes"
            element={
              <CmsModuleGate module="clientes">
                <CmsClientsEditor />
              </CmsModuleGate>
            }
          />
          <Route
            path="leads"
            element={
              <CmsModuleGate module="leads">
                <CmsLeadsEditor />
              </CmsModuleGate>
            }
          />
          <Route
            path="quejas"
            element={
              <CmsModuleGate module="quejas">
                <CmsQuejasEditor />
              </CmsModuleGate>
            }
          />
          <Route
            path="home"
            element={
              <CmsModuleGate module="home">
                <CmsHomePage />
              </CmsModuleGate>
            }
          />
          <Route
            path="footer"
            element={
              <CmsModuleGate module="footer">
                <CmsFooterEditor />
              </CmsModuleGate>
            }
          />
          <Route
            path="navigation"
            element={
              <CmsModuleGate module="navigation">
                <CmsNavigationEditor />
              </CmsModuleGate>
            }
          />
          <Route
            path="about"
            element={
              <CmsModuleGate module="about">
                <CmsAboutEditor />
              </CmsModuleGate>
            }
          />
          <Route
            path="credibility"
            element={
              <CmsModuleGate module="credibility">
                <CmsCredibilityEditor />
              </CmsModuleGate>
            }
          />
          <Route
            path="services"
            element={
              <CmsModuleGate module="services">
                <CmsServicesEditor />
              </CmsModuleGate>
            }
          />
          <Route
            path="research"
            element={
              <CmsModuleGate module="research">
                <CmsResearchEditor />
              </CmsModuleGate>
            }
          />
          <Route
            path="labsyso"
            element={
              <CmsModuleGate module="labsyso">
                <CmsLabsysoEditor />
              </CmsModuleGate>
            }
          />
          <Route
            path="identidad"
            element={
              <CmsModuleGate module="identidad">
                <CmsIdentidadPage />
              </CmsModuleGate>
            }
          />
          <Route
            path="icons"
            element={
              <CmsModuleGate module="icons">
                <CmsIconsEditor />
              </CmsModuleGate>
            }
          />
          <Route
            path="calculadoras"
            element={
              <CmsModuleGate module="calculadoras">
                <CmsCalculatorsEditor />
              </CmsModuleGate>
            }
          />
          <Route
            path="privacidad"
            element={
              <CmsModuleGate module="privacidad">
                <CmsPrivacidadEditor />
              </CmsModuleGate>
            }
          />
          <Route
            path="informes"
            element={
              <CmsInformesGate>
                <CmsInformesPage />
              </CmsInformesGate>
            }
          />
          <Route
            path="users"
            element={
              <CmsModuleGate module="users">
                <CmsUsersPage />
              </CmsModuleGate>
            }
          />
          <Route path="biblioteca" element={<Navigate to="/admin/biblioteca/usuario" replace />} />
          <Route path="documentacion" element={<Navigate to="/admin/biblioteca/usuario" replace />} />
          <Route path="biblioteca/:seccion" element={<DocsLibraryPage />} />
        </Route>
      </Route>
      <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}

function App() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  if (isAdmin) {
    return (
      <div className="min-h-screen bg-[#f3f6f2] font-body">
        <ScrollToTop />
        <AdminRoutes />
      </div>
    );
  }

  return <PublicShell />;
}

export default App;
